import { Folder, FileText, Plus, TriangleAlert, X } from "lucide-react";
import { useEffect, useRef, useState, type FC } from "react";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from "@/components/ui/dialog";
import { FieldLabel } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from "@/components/ui/select";
import { cn } from "@/lib/utils";

import {
  applyCustom,
  customSchemeOf,
  customSchemeProblem,
  LEVEL_STYLES,
  MAX_CUSTOM_LEVELS,
  MAX_PREFIX_CHARS,
  NONE_LEVEL,
  setCustomLevel,
  unlabeledNote,
  type CustomScheme,
  type LevelStyleId
} from "../exhibitStylePresets";
import { previewRows } from "../exhibitSchemePreview";
import type { ExhibitScheme } from "../types";

const INDENT = ["pl-0", "pl-4", "pl-8"];

const LEVEL_HINTS = ["Top folders and files", "Inside them", "Inside those"];

const levelHint = (depth: number) =>
  LEVEL_HINTS[Math.min(depth, LEVEL_HINTS.length - 1)];

const sameCustom = (a: CustomScheme, b: CustomScheme) =>
  a.prefix.trim() === b.prefix.trim() &&
  a.punctuation === b.punctuation &&
  a.levels.join() === b.levels.join();

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The scheme the builder starts from; saving keeps its start and citations. */
  scheme: ExhibitScheme;
  /** Where the labels apply, for the description. */
  scope: "case" | "firm";
  readOnly?: boolean;
  onSave: (scheme: ExhibitScheme) => void;
};

/**
 * Custom exhibit labels: a style per folder level (or None, for folders with
 * no label of their own), an optional prefix and the ending, with the labels
 * a sample tree would get. Only what the backend stores is offered: levels
 * join with ".", and the prefix and ending come from the first labeled level.
 */
export const ExhibitSchemeDialog: FC<Props> = ({
  open,
  onOpenChange,
  scheme,
  scope,
  readOnly = false,
  onSave
}) => {
  const [draft, setDraft] = useState(() => customSchemeOf(scheme));
  // Reset only when the dialog opens. Case Preparation rebuilds `scheme`
  // on every parent render; syncing on that identity drops an added level
  // (and every other edit) the moment the settings page re-renders.
  const wasOpen = useRef(false);
  useEffect(() => {
    if (open && !wasOpen.current) setDraft(customSchemeOf(scheme));
    wasOpen.current = open;
  }, [open, scheme]);

  const next = applyCustom(scheme, draft);
  const rows = previewRows(next);
  const changed = !sameCustom(draft, customSchemeOf(scheme));
  const prefixed = draft.prefix.trim().length > 0;
  const problem = customSchemeProblem(draft);
  const note = unlabeledNote(draft);

  const setLevel = (depth: number, id: LevelStyleId) =>
    setDraft((d) => setCustomLevel(d, depth, id));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="sm:max-w-lg"
        data-testid="exhibit-scheme-dialog"
      >
        <DialogHeader>
          <DialogTitle>Custom Exhibit Labels</DialogTitle>
          <DialogDescription>
            {scope === "firm"
              ? "Pick a style for each folder level. New cases start with these labels."
              : "Pick a style for each folder level. Saving renumbers this case."}{" "}
            Folders deeper than the last level use its style.
          </DialogDescription>
        </DialogHeader>

        <div
          className="flex flex-col gap-1.5"
          data-testid="exhibit-scheme-levels"
        >
          {draft.levels.map((id, depth) => (
            <div
              key={depth}
              className="flex items-center gap-2"
              data-testid={`exhibit-scheme-level-${depth + 1}`}
            >
              <div className="min-w-0 flex-1">
                <div className="text-base text-fg">Level {depth + 1}</div>
                <div className="text-sm text-fg-3">
                  {id === NONE_LEVEL
                    ? `${levelHint(depth)} · No Label`
                    : levelHint(depth)}
                </div>
              </div>
              <Select
                value={id}
                onValueChange={(v) => setLevel(depth, v as LevelStyleId)}
                disabled={readOnly}
              >
                <SelectTrigger
                  size="sm"
                  className="w-36"
                  aria-label={`Level ${depth + 1} Style`}
                  data-testid={`exhibit-scheme-level-${depth + 1}-style`}
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {LEVEL_STYLES.map((s) => (
                    <SelectItem
                      key={s.id}
                      value={s.id}
                      data-testid={`exhibit-scheme-style-${s.id}`}
                    >
                      {s.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {readOnly ? null : (
                <Button
                  variant="ghost"
                  size="icon-sm"
                  aria-label={`Remove Level ${depth + 1}`}
                  disabled={draft.levels.length === 1}
                  onClick={() =>
                    setDraft((d) => ({
                      ...d,
                      levels: d.levels.filter((_, i) => i !== depth)
                    }))
                  }
                  data-testid={`exhibit-scheme-level-${depth + 1}-remove`}
                >
                  <X />
                </Button>
              )}
            </div>
          ))}
          {readOnly || draft.levels.length >= MAX_CUSTOM_LEVELS ? null : (
            <Button
              variant="ghost"
              size="sm"
              className="-ml-1.5 self-start"
              onClick={() =>
                setDraft((d) => ({
                  ...d,
                  levels: [...d.levels, d.levels[d.levels.length - 1] ?? "1"]
                }))
              }
              data-testid="exhibit-scheme-add-level"
            >
              <Plus />
              Add Level
            </Button>
          )}
        </div>

        <div className="flex items-end gap-2">
          <div className="flex min-w-0 flex-1 flex-col gap-1">
            <FieldLabel
              htmlFor="exhibit-scheme-prefix"
              className="text-sm font-normal text-fg-3"
            >
              Prefix
            </FieldLabel>
            <Input
              id="exhibit-scheme-prefix"
              value={draft.prefix}
              maxLength={MAX_PREFIX_CHARS}
              placeholder="None, e.g. Exhibit"
              readOnly={readOnly}
              onChange={(e) =>
                setDraft((d) => ({ ...d, prefix: e.target.value }))
              }
              data-testid="exhibit-scheme-prefix"
            />
          </div>
          <div className="flex flex-col gap-1">
            <FieldLabel
              htmlFor="exhibit-scheme-ending"
              className="text-sm font-normal text-fg-3"
            >
              Ending
            </FieldLabel>
            <Select
              value={draft.punctuation}
              onValueChange={(v) =>
                setDraft((d) => ({
                  ...d,
                  punctuation: v === ")" ? ")" : "."
                }))
              }
              disabled={readOnly || prefixed}
            >
              <SelectTrigger
                id="exhibit-scheme-ending"
                className="w-28"
                aria-label="Ending"
                data-testid="exhibit-scheme-ending"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value=".">Period (A.)</SelectItem>
                <SelectItem value=")">Parenthesis (A))</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        {prefixed ? (
          <p className="-mt-2 text-sm text-fg-3">
            With a prefix, labels end without punctuation.
          </p>
        ) : null}
        {problem && !readOnly ? (
          <p
            className="-mt-1 flex items-start gap-1.5 text-sm text-warn"
            data-testid="exhibit-scheme-problem"
          >
            <TriangleAlert className="mt-0.5 size-3.5 shrink-0" aria-hidden />
            {problem}
          </p>
        ) : null}

        <div className="flex flex-col gap-1">
          <span className="text-sm text-fg-3">Preview</span>
          {note ? (
            <span
              className="text-sm text-fg-4"
              data-testid="exhibit-scheme-unlabeled-note"
            >
              {note}
            </span>
          ) : null}
          <ul
            className="flex flex-col gap-0.5 rounded-md bg-fill-1 p-2"
            data-testid="exhibit-scheme-preview"
          >
            {rows.map((row) => (
              <li
                key={row.id}
                className={cn(
                  "flex items-center gap-1.5 text-sm",
                  INDENT[Math.min(row.depth, INDENT.length - 1)]
                )}
                data-testid={`exhibit-scheme-preview-${row.id}`}
              >
                {row.folder ? (
                  <Folder className="size-3.5 shrink-0 text-fg-4" aria-hidden />
                ) : (
                  <FileText
                    className="size-3.5 shrink-0 text-fg-4"
                    aria-hidden
                  />
                )}
                {row.label ? (
                  <span className="shrink-0 font-mono text-fg-2 tabular-nums">
                    {row.label}
                  </span>
                ) : (
                  <span className="shrink-0 text-fg-4">No Label</span>
                )}
                <span className="min-w-0 truncate text-fg-3">{row.name}</span>
              </li>
            ))}
          </ul>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            {readOnly ? "Close" : "Cancel"}
          </Button>
          {readOnly ? null : (
            <Button
              variant="primary"
              disabled={!changed || problem !== null}
              onClick={() => {
                onSave(next);
                onOpenChange(false);
              }}
              data-testid="exhibit-scheme-save"
            >
              Save Labels
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
